import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { Typography, useTheme, CircularProgress, keyframes } from "@mui/material";
import { FormPropertyProps } from "../../FormProperty";
import { formatUnitAbbreviation, formatNumberByType } from "src/utils/formatting";
import { useEntityScores } from "src/context/Scores/useEntityScores";
import { SSQTextInput } from "src/components/SSQ/FormFields/SSQTextInput";
import { manageTooltipText } from "src/utils/tooltips";
import { sanitizeRichTextHtml } from "src/components/HTMLBox/sanitizeRichTextHtml";
import { getDisplayValueForScore } from "src/utils/scores";
import { OlapScoresService } from "src/context/OlapScores/OlapScoresService";
import { usePropertyContext } from "../../context/usePropertyContext";
import { FieldValueLayout } from "../FieldValueLayout";
import { PropertyUpdateOutcome } from "../../context/context";
import { UnitConversion } from "src/api/client-api/post/postSchema";
import { buildPropertyValidationKey } from "src/utils/validation";
import { exceededBound } from "src/utils/unitConversion";

const shake = keyframes`
    0%, 100% { transform: translateX(0); }
    10%, 30%, 50%, 70%, 90% { transform: translateX(-4px); }
    20%, 40%, 60%, 80% { transform: translateX(4px); }
`;

export const Numeric = (props: FormPropertyProps) => {
    const { t, i18n } = useTranslation();
    const { selectedScorePrecision } = useEntityScores();

    const theme = useTheme();
    const { property, position, showSubscore, dimensionValue } = props;

    const { post, getElement, onUpdate, scoreUnit, getValidationError, canEdit, ssqVersionStructureId } =
        usePropertyContext();
    const element = getElement(property.id, position);

    const validationState = getValidationError(property.id, position);

    const _defaultValue = (property.defaultValue ?? "").replace(/\s/g, "").replace(/,/g, ".");
    const formattedDefaultValue = formatNumberByType(_defaultValue, property.unit?.title, i18n.resolvedLanguage, 20);
    const initialValue = (element?.value ?? "").replace(/\s/g, "").replace(/,/g, ".");

    const [previousValue, setPreviousValue] = useState(initialValue);
    const [value, setValue] = useState(initialValue);
    const [genericValueSelected, setGenericValueSelected] = useState<boolean>(element?.isGenericValue ?? false);
    const [rangeError, setRangeError] = useState(false);

    const genericValue = property.genericValue;

    const checkRangeError = (val?: string) => {
        if (val === "" || val === undefined) return false;
        return exceededBound(Number(val), property.minValue, property.maxValue) !== null;
    };

    useEffect(() => {
        setValue(initialValue);
    }, [initialValue]);

    useEffect(() => {
        const hasError = checkRangeError(value);
        if (hasError && rangeError === false) {
            setRangeError(hasError);
        }
        if (hasError === false) {
            setRangeError(false);
        }
    }, [value, property.minValue, property.maxValue]);

    useEffect(() => {
        const hasValidationError =
            validationState.hasValidationMessages && validationState.validationSeverity === "error";
        if (hasValidationError) {
            setValue(previousValue);
            // setErrorKey((prev) => prev + 1);
        }
    }, [
        validationState.hasValidationMessages,
        validationState.validationSeverity,
        validationState.validationMessages,
        previousValue,
    ]);

    if (!post) return <CircularProgress />;

    const unit = formatUnitAbbreviation(property.unit);

    const updatePropertyValue = (
        value?: string,
        isGenericValue?: boolean,
        reportOutcome?: (outcome: PropertyUpdateOutcome) => void,
        unitConversion?: UnitConversion,
    ) => {
        if (checkRangeError(value)) {
            reportOutcome?.({ saved: false, messages: [] });
            return setValue(previousValue);
        }

        onUpdate(
            {
                postId: post.id,
                propertyId: Number(property.id),
                parentPropertyId:
                    property.parent ?
                        typeof property.parent === "string" ?
                            Number(property.parent.split("/").pop())
                        :   property.parent.id
                    :   undefined,
                value: value ?? "",
                position: Number(position),
                isGenericValue: isGenericValue,
                unitConversion,
            },
            reportOutcome,
        );
    };

    const isValidNumber = (value: unknown) => {
        return typeof value === "number" && !isNaN(value);
    };

    const defaultValueText = property.readonly ? t("cette_valeur_est_en_lecture_seule") : t("valeur_par_defaut");

    const tooltipText = manageTooltipText((coalescedText) => {
        return <div dangerouslySetInnerHTML={{ __html: sanitizeRichTextHtml(coalescedText) }} />;
    }, property);

    const isDefaultActivated = !property.readonly && _defaultValue && !genericValueSelected;
    const defaultValue = isDefaultActivated ? formattedDefaultValue : undefined;

    const propertyScoreValue = OlapScoresService.normalizeNumericalScore(dimensionValue?.score.value);
    const shouldDisplayScore = propertyScoreValue !== null && showSubscore === true;

    const scoreProperty = {
        score:
            shouldDisplayScore !== false ? getDisplayValueForScore(propertyScoreValue, selectedScorePrecision) : null,
        unit: scoreUnit,
    };

    const onCalculate = (newValue: string) => {
        if (newValue === previousValue) return;
        updatePropertyValue(newValue, undefined);
    };

    const isWarning =
        !rangeError && validationState.hasValidationMessages && validationState.validationSeverity === "warning";
    const isError =
        rangeError || (validationState.hasValidationMessages && validationState.validationSeverity === "error");

    const getInputSx = () => {
        if (!isWarning && !isError) return undefined;

        const color = isError ? theme.custom.red : theme.custom.orange;
        const backgroundColor = isError ? theme.custom.red[100] : theme.custom.orange[100];
        const backgroundColorHover = isError ? theme.custom.red[200] : theme.custom.orange[200];

        const baseStyles = {
            border: `1px solid ${color[600]}`,
            backgroundColor: backgroundColor,
            ":hover": { backgroundColor: backgroundColorHover },
        };

        return {
            "&.MuiInputBase-root":
                isError ?
                    { ...baseStyles, animation: `${shake} 0.4s ease-in-out`, color: theme.palette.text.primary }
                :   baseStyles,
        };
    };

    return (
        <FieldValueLayout
            property={property}
            element={element}
            layoutOptions={{
                defaultValue: defaultValue ?? null,
                genericValue: genericValue ?? null,
                isGenericValueSelected: genericValueSelected,
                dimensionValue: dimensionValue,
                forceHideScore: showSubscore === false,
                setDefaultValue: () => {
                    if (defaultValue) {
                        // Save the raw value (`defaultValue` is only the localized display
                        // string) and skip onCalculate: its `previousValue` guard is meant to
                        // drop no-op blurs, and would swallow a reset back to the default.
                        setValue(_defaultValue);
                        updatePropertyValue(_defaultValue, undefined);
                    }
                },
                setGenericValue: () => {
                    const v = !genericValueSelected ? genericValue : "";
                    setGenericValueSelected((prevState) => !prevState);
                    setValue(v ?? "");
                    updatePropertyValue(v ?? undefined, !genericValueSelected);
                },
                unitConverter: {
                    key: buildPropertyValidationKey(property.id, position, ssqVersionStructureId),
                    onApply: (canonicalValue, unitConversion) =>
                        new Promise((resolve) => {
                            setPreviousValue(value);
                            updatePropertyValue(
                                canonicalValue,
                                undefined,
                                (outcome) => {
                                    // Only once saved: the validation-error revert restores previousValue,
                                    // so a rejected conversion must leave the previous answer in place.
                                    if (outcome.saved) {
                                        setPreviousValue(canonicalValue);
                                        setValue(canonicalValue);
                                    }
                                    resolve(outcome);
                                },
                                unitConversion,
                            );
                        }),
                },
            }}
        >
            <SSQTextInput
                onCalculate={onCalculate}
                // score={scoreProperty}
                // tooltipText={tooltipText}
                enableLocalization={true}
                locale={i18n.resolvedLanguage || "en"}
                type={property.field?.type}
                unitTitle={property.unit?.title}
                precision={property.precision ?? undefined}
                // defaultOption={
                //     defaultValue === undefined ? undefined : (
                //         {
                //             value: _defaultValue,
                //             label: defaultValueText,
                //         }
                //     )
                // }
                value={value !== "_empty_" ? value : ""}
                onChange={(newValue) => {
                    setValue(newValue);
                }}
                // unit={unit}
                disabled={property.readonly || !canEdit || genericValueSelected}
                error={isError}
                ExtraInputProps={{
                    onFocus: (e) => {
                        const isFocusDirect = !e.relatedTarget;

                        if (!isFocusDirect) {
                            return;
                        }

                        setPreviousValue(value);
                    },
                    ...(getInputSx() && { sx: getInputSx() }),
                }}
            />
            {rangeError && (
                <Typography variant="caption" color={theme.custom.red[600]}>
                    {(() => {
                        if (isValidNumber(property.minValue) && isValidNumber(property.maxValue)) {
                            return t("form.min_max.error", { min: property.minValue, max: property.maxValue });
                        }
                        if (isValidNumber(property.minValue)) {
                            return t("form.min.error", { min: property.minValue });
                        }
                        if (isValidNumber(property.maxValue)) {
                            return t("form.max.error", { max: property.maxValue });
                        }
                        return null;
                    })()}
                </Typography>
            )}
        </FieldValueLayout>
    );
};
